import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { RouteReuseStrategy, Router } from '@angular/router';
import { catchError, from, retry, switchMap, throwError, timer, timeout } from 'rxjs';

import { AuthService } from '../auth/auth.service';
import { RequireFullAuthService } from '../auth/require-full-auth.service';
import { FeedbackService } from '../feedback/feedback.service';
import { clientErrorMessage } from './client-error-message';

const IDEMPOTENT_TIMEOUT_MS = 15_000;
const IDEMPOTENT_RETRY_DELAY_MS = 300;
const SILENT_ERROR_PATHS = [
  '/auth/me',
  '/auth/validate-password',
  '/auth/guest-login',
  '/auth/membership/verify',
  '/auth/login',
  '/auth/logout',
  '/cms/blocks/',
  '/static-page/data/titulos_de_producto',
  '/me/wishlist',
  '/me/addresses',
  '/me/recommendations',
  '/me/checkout',
];

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const feedback = inject(FeedbackService);
  const fullAuth = inject(RequireFullAuthService);
  const router = inject(Router);
  const reuseStrategy = inject(RouteReuseStrategy);

  const source = isIdempotent(req.method)
    ? next(req).pipe(timeout({ each: IDEMPOTENT_TIMEOUT_MS }), retry(idempotentRetryConfig()))
    : next(req);

  return source.pipe(
    catchError((error: unknown) => {
      if (fullAuth.isFullAuthRequiredError(error) && shouldRetryAfterFullAuth(req)) {
        return from(fullAuth.ensureFullAuth({ force: true })).pipe(
          switchMap((allowed) => (allowed ? next(req) : throwError(() => error))),
        );
      }

      if (error instanceof HttpErrorResponse && shouldHandleExpiredSession(req.url, error.status)) {
        handleExpiredSession(auth, router, reuseStrategy);
      }

      if (error instanceof HttpErrorResponse && shouldShowGlobalToast(req.url, error.status)) {
        feedback.error(
          clientErrorMessage(
            error,
            'Estamos teniendo problemas para atender la solicitud. Inténtalo en unos segundos.',
          ),
        );
      } else if (isTimeoutError(error) && shouldShowGlobalToast(req.url, 0)) {
        feedback.error('La solicitud está tardando demasiado. Inténtalo nuevamente.');
      }
      return throwError(() => error);
    }),
  );
};

function idempotentRetryConfig(): Parameters<typeof retry>[0] {
  return {
    count: 1,
    delay: (error) =>
      isRetryableError(error)
        ? timer(IDEMPOTENT_RETRY_DELAY_MS)
        : throwError(() => error as unknown),
  };
}

function isIdempotent(method: string): boolean {
  return ['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase());
}

function isRetryableError(error: unknown): boolean {
  if (isTimeoutError(error)) return true;
  if (!(error instanceof HttpErrorResponse)) return false;
  return (
    error.status === 0 ||
    error.status === 408 ||
    error.status === 502 ||
    error.status === 503 ||
    error.status === 504
  );
}

function shouldHandleExpiredSession(url: string, status: number): boolean {
  if (status !== 401) return false;
  if (SILENT_ERROR_PATHS.some((path) => url.includes(path))) return false;
  return true;
}

function shouldShowGlobalToast(url: string, status: number): boolean {
  if (SILENT_ERROR_PATHS.some((path) => url.includes(path))) return false;
  if (url.includes('/me/cart')) return false;
  return status === 0 || status === 401 || status >= 500;
}

function shouldRetryAfterFullAuth(req: { readonly method: string; readonly url: string }): boolean {
  if (['GET', 'HEAD', 'OPTIONS'].includes(req.method.toUpperCase())) return false;
  const url = req.url;
  if (url.includes('/auth/validate-password')) return false;
  return url.includes('/me/checkout') || url.includes('/me/addresses');
}

function handleExpiredSession(
  auth: AuthService,
  router: Router,
  reuseStrategy: RouteReuseStrategy,
): void {
  auth.clearSession();
  clearRouteReuse(reuseStrategy);
  const redirect = safeRedirect(router.url);
  void router.navigate(['/ingreso/ingresar'], { queryParams: { redirect } });
}

function clearRouteReuse(reuseStrategy: RouteReuseStrategy): void {
  const candidate = reuseStrategy as RouteReuseStrategy & { clear?: () => void };
  candidate.clear?.();
}

function safeRedirect(url: string): string {
  if (!url || !url.startsWith('/') || url.startsWith('//') || url.startsWith('/ingreso')) {
    return '/inicio';
  }
  return url;
}

function isTimeoutError(error: unknown): boolean {
  return error instanceof Error && error.name === 'TimeoutError';
}
